import { Divider } from '@mui/material';
import cn from 'classnames';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectIsSuperAdmin } from '../../store/selectors/auth';
import { selectFulfillmentCenterById } from '../../store/selectors/fulfillmentCenters';

import styles from './FCDetails.module.scss';

interface IProps {
  productSku?: string;
  fulfillmentCenterId?: string;
  isHighlighted?: boolean;
  isDisabled?: boolean;
  className?: string;
  count?: number;
  isOutOfStock?: boolean;
  withLabel?: boolean;
  isHorizontal?: boolean;
}

const FCDetails: React.FC<IProps> = ({
  fulfillmentCenterId,
  productSku,
  isHighlighted,
  className,
  isDisabled,
  count,
  isOutOfStock,
  withLabel = false,
  isHorizontal = false,
}) => {
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const { name: fcName } = useSelector(selectFulfillmentCenterById(fulfillmentCenterId)) || {};
  const hasNotAvailableCount = useMemo(() => count === -1, [count]);
  const showCount = useMemo(() => isOutOfStock, [isOutOfStock]);

  const divider = useMemo(() => {
    return <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />;
  }, []);

  const shouldShowDividerAfterProductSku = useMemo(
    () => productSku && isHorizontal && divider,
    [productSku, isHorizontal, divider],
  );
  const shouldShowDividerAfterFcName = useMemo(
    () => isSuperAdmin && fcName && isHorizontal && divider,
    [isSuperAdmin, fcName, isHorizontal, divider],
  );

  return productSku || fcName ? (
    <div
      className={cn(
        styles.fcDetails,
        className,
        { [styles.highlighted]: isHighlighted },
        { [styles.disabled]: isDisabled },
        { [styles.horizontal]: isHorizontal },
      )}
    >
      {productSku && (
        <span title={`SKU: ${productSku}`}>
          {withLabel && <span>SKU:&nbsp;</span>}
          {productSku}
        </span>
      )}
      {isSuperAdmin && fcName && (
        <>
          {shouldShowDividerAfterProductSku}
          <span className={styles.fcLabel} title={`FC: ${fcName}`}>
            {withLabel && <span>Warehouse:&nbsp;</span>}
            {fcName}
          </span>
        </>
      )}
      {showCount ? (
        <>
          {shouldShowDividerAfterFcName}
          <span className={styles.instockLabelError} title={`In stock: ${count}`}>{`In stock: ${
            hasNotAvailableCount ? 'n/a' : count
          }`}</span>
        </>
      ) : null}
    </div>
  ) : null;
};

export default FCDetails;
